import { Button, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { OrganisationService } from '../../Component/Api/Organisation/OrganisationService';
import { DataTable } from '../../Component/Common/Components/DataTable/DataTable';
import { UserAdminRole } from '../../Component/Common/Constants/UserRoles';
import { ColumnSortOption } from '../../Component/Model/Dto/ColumnSortOption';
import { Organisation } from '../../Component/Model/Dto/Organisation/Organisation';
import { EntriesPerPage } from '../../Component/Model/Table/Table';
import { UserAtom, userRolesState } from '../../State/user.Atom';
import { OrganisationModal } from './OrganisationModal';
import { OrganisationTableMapper } from './OrganisationTableColumns';
import { AssignModal } from '../BEEScorecards/AssignModal';
import { useHistory } from 'react-router-dom';
import { OrganisationAtom } from '../../State/Organisation.Atom';
import { WSPScorecardService } from "../../Component/Api/WSPScorecard/WSPScorecardService";

export const OrganisationPage = () => {
  const [organisationData, setOrganisationData] = useState<Organisation[]>([]);
  const [entriesPerPage, setEntriesPerPage] = useState<EntriesPerPage>({ defaultValue: 5 });
  const [totalItemsCount, setTotalItemsCount] = useState<number>(0);
  const [filteredItemCount, setFilteredItemCount] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [columnSortOptions, setColumnSortOptions] = useState<ColumnSortOption>();
  const [openCreateOrganisation, setOpenCreateOrganisation] = useState<boolean>(false);
  const [organisationSelected, setOrganisationSelected] = useState<number>(0);
  const [showAssignUserModal, setShowAssignUserModal] = useState<boolean>(false);
  const [hasWSPScorecardLicenses, setHasWSPScorecardLicenses] = useState<boolean>(false);
  const setOrganisationAtom = useSetRecoilState(OrganisationAtom);
  const currentUser = useRecoilValue(UserAtom);
  const userRoles = useRecoilValue(userRolesState);
  const history = useHistory();

  useEffect(() => {
    fetchOrganisations();
  }, [entriesPerPage,
    pageNumber,
    columnSortOptions,
  ]);

  const fetchOrganisations = async () => {
    setIsLoading(true);
    try {
      let organisationResult = await OrganisationService.searchOrganisations(columnSortOptions,"", pageNumber, entriesPerPage.defaultValue);
      setOrganisationData(organisationResult.data.results);
      setFilteredItemCount(organisationResult.data.filteredItemsCount);
      setTotalItemsCount(organisationResult.data.totalItemsCount);
    } catch (error) {
      console.log(error);
      setTotalItemsCount(1);
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    WSPScorecardService.getWSPScorecardLicenses()
        .then((scorecardLicenses) => {
          setHasWSPScorecardLicenses(scorecardLicenses.data.length > 0);
        })
  }, []);

  
  const handleAssignUser = (organisationId: number, organisationName: string) => {
    setOrganisationSelected(organisationId);
    setShowAssignUserModal(true);
  }

  const closeAssignUserModal = () => {
    setOrganisationSelected(0);
    setShowAssignUserModal(false);
  }

  const handleEdit = (id: number) => {
    setOrganisationSelected(id);
    setOpenCreateOrganisation(true);
  }

  const openCreateModal = () => {
    setOrganisationSelected(0);
    setOpenCreateOrganisation(true)
  }

  const closeCreateModal = () => {
    setOrganisationSelected(0);
    setOpenCreateOrganisation(false);
    fetchOrganisations();
  }

  const entriesPerPageChanged = (entriesPerPage: number) => {
    setEntriesPerPage({ defaultValue: entriesPerPage });
    setPageNumber(1);
  };

  const pageNumberChanged = (pageNumber: number) => {
    setPageNumber(pageNumber);
  };

  const columnSortingChanged = (sortOption: ColumnSortOption) => {
    setColumnSortOptions(sortOption)
    setPageNumber(1);
  };

  const openPayrollIntegration = (id: number, name: string) => {
    setOrganisationAtom({ OrganisationId: id ?? -1, OrganisationName: name, ShowPayrollIntegrationMenuItems: true});
        history.push(`/organisation/payrollConnections`);
  }
  const getSetaTypeId = (organisation: Organisation) => {
      return organisation.setaTypeId === null ? "0" : (organisation.setaTypeId?.toString() || "0");
  }
  const openManagePeople = (organisation: Organisation) => {
    setOrganisationAtom({ OrganisationId: organisation.organisationId ?? -1, OrganisationName: organisation.registrationName, SetaTypeId: getSetaTypeId(organisation), ShowPayrollIntegrationMenuItems: false });
    history.push(`/organisation/people`);
  }

  const openManageTraining = (organisation: Organisation) => {
    setOrganisationAtom({ OrganisationId: organisation.organisationId ?? -1, OrganisationName: organisation.registrationName, SetaTypeId: getSetaTypeId(organisation), ShowPayrollIntegrationMenuItems: false });
    history.push(`/organisation/training/External`);
  }

  const openFetchPayrollData = async (organisation: Organisation) => {
    setOrganisationAtom({ OrganisationId: organisation.organisationId ?? -1, OrganisationName: organisation.registrationName, SetaTypeId: getSetaTypeId(organisation), ShowPayrollIntegrationMenuItems: true });
    history.push(`/organisation/payrollData/fetchData`);
  }
  
  const organisationTableData = OrganisationTableMapper(organisationData, handleEdit, handleAssignUser, openPayrollIntegration, openManagePeople, openManageTraining, openFetchPayrollData, hasWSPScorecardLicenses, currentUser.enablePayrollIntegration);

  return (
    <>
      <Typography variant="h4" style={{ paddingBottom: "16px" }}>
        Organisations
      </Typography>
      <Typography variant="body2" style={{ paddingBottom: "16px" }}>
        Organisations are used to represent the structure of your business.
      </Typography>
      {userRoles.includes(UserAdminRole) &&
        <Button variant='contained' onClick={() => openCreateModal()}>Capture Organisation</Button>
      }
      {openCreateOrganisation &&
        <OrganisationModal open={openCreateOrganisation} closeModal={closeCreateModal} organisationId={organisationSelected} />}
      <DataTable
        table={organisationTableData}
        entriesPerPage={entriesPerPage}
        pageNumber={pageNumber}
        filteredItemsCount={filteredItemCount}
        totalItemsCount={totalItemsCount}
        isLoading={isLoading}
        manualSorting={true}
        entriesPerPageChanged={entriesPerPageChanged}
        pageNumberChanged={pageNumberChanged}
        columnSortingChanged={columnSortingChanged} />
      {showAssignUserModal && <AssignModal id={organisationSelected} isOrganisationUserAccess={true} showAssignModal={showAssignUserModal} closeAssignModal={closeAssignUserModal} />}    </>
  )
}